/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import type { Space } from '../../controllers/space-store/types';

import messages from './messages';

const styles = cssMap({
	errorContainer: {
		marginTop: token('space.200'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.danger'),
		color: token('color.text.danger'),
		font: token('font.body'),
	},
});

type DeleteSpaceModalProps = {
	space: Space;
	isOpen: boolean;
	onClose: () => void;
	onDeleteConfirm: (spaceId: string) => Promise<void>;
};

export const DeleteSpaceModal = ({
	space,
	isOpen,
	onClose,
	onDeleteConfirm,
}: DeleteSpaceModalProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | undefined>(undefined);

	const handleDelete = async () => {
		setIsSubmitting(true);
		try {
			await onDeleteConfirm(space.id);
			setError(undefined);
			onClose();
		} catch (e) {
			setError(formatMessage(messages.genericErrorMessage));
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleCancel = () => {
		setError(undefined);
		onClose();
	};

	return (
		<ModalTransition>
			{isOpen && (
				<Modal onClose={handleCancel} width="small" testId="deleteSpaceModal">
					<ModalHeader hasCloseButton>
						<ModalTitle>
							{formatMessage(messages.deleteSpaceTitle, { name: space.name })}
						</ModalTitle>
					</ModalHeader>
					<ModalBody>
						<Stack>
							<Box>{formatMessage(messages.modalBody)}</Box>
							{!!error?.length && <Box xcss={styles.errorContainer}>{error}</Box>}
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Button appearance="subtle" onClick={handleCancel}>
							{formatMessage(messages.cancelButtonLabel)}
						</Button>
						<Button isLoading={isSubmitting} appearance="danger" onClick={handleDelete}>
							{formatMessage(messages.deleteButtonLabel)}
						</Button>
					</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};
